import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import cn from 'classnames';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { previewProductSinkDetach, startProductSinkDetach, getProductSinkDetachRuns, revertProductSinkAgentMatches } from '../../api/url_data';
import { getProducts } from '../../api/products';
import { getSmartImage } from '../../helpers/helpers';
import CatalogToolSubNav from '../CatalogTool/CatalogToolSubNav';
import './ProductSinkDetach.scss';

const RUNS_REFRESH_MS = 10000;
const URLS_PER_PAGE = 100;

const readParamsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const productId = parseInt(params.get('productId'), 10);
  return { productId: productId > 0 ? productId : null, domain: params.get('domain') || '' };
};

const writeParamsToUrl = (productId, domain) => {
  const params = new URLSearchParams();
  if (productId) params.set('productId', String(productId));
  if (domain) params.set('domain', domain);
  const qs = params.toString();
  window.history.pushState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
};

const STRANDED_URL_STATE_LABELS = { dismissed: 'dismissed', unmatched: 'unmatched', elsewhere: 'matched to another product' };
const describeStrandedUrlStates = byUrlState =>
  Object.entries(byUrlState)
    .map(([state, count]) => `${count} ${STRANDED_URL_STATE_LABELS[state] || state}`)
    .join(', ');

const ProductSinkDetach = () => {
  const [productId, setProductId] = useState('');
  const [domain, setDomain] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [report, setReport] = useState(null);
  const [excludedIds, setExcludedIds] = useState([]);
  const [routeIgnoredDomains, setRouteIgnoredDomains] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [runs, setRuns] = useState([]);
  const [hasLoadedRuns, setHasLoadedRuns] = useState(false);

  const fetchRuns = useCallback(async () => {
    try {
      const response = await getProductSinkDetachRuns();
      setRuns(response.data || []);
      setHasLoadedRuns(true);
    } catch {
      // Silent: the poll retries and a toast every ten seconds would be noise.
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const hasActiveRun = runs.some(run => run.status === 'pending' || run.status === 'running');
  useEffect(() => {
    if (!hasActiveRun) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      fetchRuns();
    }, RUNS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [hasActiveRun, fetchRuns]);

  const [searchVal, setSearchVal] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const latestSearchId = useRef(0);

  const clearPreview = () => {
    setReport(null);
    setExcludedIds([]);
    setRouteIgnoredDomains(false);
    setFilter('');
    setPage(0);
  };

  const previewFor = useCallback(async (idNum, domainStr, { pushUrl = true } = {}) => {
    setIsPreviewing(true);
    setReport(null);
    setExcludedIds([]);
    setRouteIgnoredDomains(false);
    setFilter('');
    setPage(0);
    try {
      const response = await previewProductSinkDetach({ Product_id: idNum, domain: domainStr || undefined });
      setReport(response.data);
      if (pushUrl) writeParamsToUrl(idNum, domainStr);
      if (!response.data.hasWork) {
        window.ALERT.info('No matched URLs or stranded pins found for that product');
      }
    } catch (error) {
      window.ALERT.error(error || 'Failed to preview');
    } finally {
      setIsPreviewing(false);
    }
  }, []);

  const handlePreview = () => previewFor(parseInt(productId), domain.trim());

  // ?productId=&domain= round-trips so previews are linkable and survive reloads.
  useEffect(() => {
    const loadFromUrl = () => {
      const params = readParamsFromUrl();
      if (!params.productId) return;
      setProductId(String(params.productId));
      setDomain(params.domain);
      previewFor(params.productId, params.domain, { pushUrl: false });
    };
    loadFromUrl();
    window.addEventListener('popstate', loadFromUrl);
    return () => window.removeEventListener('popstate', loadFromUrl);
  }, [previewFor]);

  const searchProductsDebounced = useMemo(() => AwesomeDebouncePromise(query => getProducts({ query, pageSize: 12 }), 250), []);

  const handleSearchChange = async value => {
    setSearchVal(value);
    const trimmed = value.trim();
    // Require 3 chars before searching; digit-only is exempt (product-ID lookup, no Algolia).
    // Bumping the id below the gate discards any pending or in-flight search for a longer query,
    // so the id is captured here rather than inside the debounced fn at fire time.
    if (trimmed.length < 3 && !/^\d+$/.test(trimmed)) {
      latestSearchId.current += 1;
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    const searchId = ++latestSearchId.current;
    setIsSearching(true);
    try {
      const { products } = await searchProductsDebounced(trimmed);
      if (searchId !== latestSearchId.current) return;
      setSearchResults(products || []);
      setIsSearching(false);
    } catch {
      if (searchId !== latestSearchId.current) return;
      setIsSearching(false);
    }
  };

  const selectSearchResult = product => {
    setSearchVal('');
    setSearchResults([]);
    setProductId(String(product.id));
    previewFor(product.id, domain.trim());
  };

  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);
  const toggleExcluded = urlDataId => {
    setExcludedIds(ids => (ids.includes(urlDataId) ? ids.filter(id => id !== urlDataId) : [...ids, urlDataId]));
  };

  const detachRows = report?.detach.rows || [];
  const excludedPinCount = useMemo(
    () => detachRows.reduce((sum, row) => (excludedSet.has(row.urlDataId) ? sum + row.pinCount : sum), 0),
    [detachRows, excludedSet]
  );
  const adjustedUrlCount = report ? report.detach.count - excludedIds.length : 0;
  const strandedPinCount = report?.pins.stranded || 0;
  const adjustedPinCount = report ? report.pins.onDetachedStems - excludedPinCount + strandedPinCount : 0;

  const filteredRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return detachRows;
    return detachRows.filter(
      row =>
        row.urlStem.toLowerCase().includes(needle) ||
        (row.crawledName || '').toLowerCase().includes(needle) ||
        row.pinSamples.some(pin => (pin.title || '').toLowerCase().includes(needle))
    );
  }, [detachRows, filter]);
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / URLS_PER_PAGE));
  const pageRows = filteredRows.slice(page * URLS_PER_PAGE, (page + 1) * URLS_PER_PAGE);

  const ignoredStemCount = report?.ignoredDomains?.stemCount || 0;

  const handleDetach = () => {
    const ignoredNote = ignoredStemCount
      ? routeIgnoredDomains
        ? ` ${ignoredStemCount} URLs on ignored domains are included.`
        : ` ${ignoredStemCount} URLs on ignored domains are left out and go nowhere.`
      : '';
    confirmAlert({
      title: 'Detach this product?',
      message: `This will unlink ${adjustedUrlCount} URLs, ~${adjustedPinCount} pins and ${report.variants.toDetach} variants from "${report.productTitle}"${
        excludedIds.length ? ` (${excludedIds.length} URLs kept as correct)` : ''
      }. Old values are saved and the URLs are queued for the agentic pin matcher.${ignoredNote}`,
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        { label: 'Detach', onClick: runDetach }
      ]
    });
  };

  // Queue from the previewed report, never the live inputs — they can have been edited
  // since the preview, and the confirm dialog describes the report.
  const runDetach = async () => {
    setIsStarting(true);
    try {
      const response = await startProductSinkDetach({
        Product_id: report.productId,
        domain: report.domain || undefined,
        excludedUrlDataIds: excludedIds,
        routeIgnoredDomains
      });
      if (response.data.alreadyRunning) {
        window.ALERT.info('A detach for this product is already queued or running');
      } else {
        window.ALERT.success('Detach queued — watch the runs table below');
      }
      clearPreview();
      fetchRuns();
    } catch (error) {
      window.ALERT.error(error || 'Failed to queue detach');
    } finally {
      setIsStarting(false);
    }
  };

  const handleRevertAgentMatches = run => {
    confirmAlert({
      title: 'Undo the agent matches from this run?',
      message: `All ${run.agentOutcomes.applied} matches the agent applied for run ${run.id} are removed and the URLs go to the front of the human queue.`,
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        {
          label: 'Undo matches',
          onClick: async () => {
            try {
              const response = await revertProductSinkAgentMatches(run.id);
              window.ALERT.success(
                response.data.alreadyQueued
                  ? 'An undo for this run is already queued or running'
                  : 'Undo queued — the applied count below drains as it runs'
              );
              fetchRuns();
            } catch (error) {
              window.ALERT.error(error || 'Failed to queue the undo');
            }
          }
        }
      ]
    });
  };

  const keepReasons = report
    ? Object.entries(report.keep.byReason)
        .map(([reason, count]) => `${count} ${reason}`)
        .join(', ') || 'none'
    : '';

  return (
    <div className='product-sink-detach-container'>
      <header className='product-sink-detach-header'>
        <CatalogToolSubNav />
        <h1>Detach Product Sink</h1>
        <p className='subtitle'>
          A "sink" is a product that bad bulk matching wrongly attached many URLs, pins and variants to. Detaching unlinks everything that
          doesn't provably belong; the URLs are queued for the agentic pin matcher to re-match. Every change is saved first, so it can be
          reverted.
        </p>
      </header>

      <div className='form-section'>
        <div className='form-fields'>
          <label className='product-search'>
            Find product
            <input
              type='text'
              value={searchVal}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder='Search by product name or ID'
            />
            {(isSearching || !!searchResults.length) && !!searchVal.trim() && (
              <div className='search-results'>
                {isSearching && <div className='search-status'>Searching...</div>}
                {!isSearching && !searchResults.length && <div className='search-status'>No products found</div>}
                {searchResults.map(product => (
                  <button key={product.id} className='search-result' onClick={() => selectSearchResult(product)}>
                    {product.image && <img src={getSmartImage(product.image)} alt='' />}
                    <span className='result-title'>{product.title}</span>
                    <span className='result-meta'>
                      {product.brand ? `${product.brand} · ` : ''}#{product.id}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </label>
          <label>
            Product ID
            <input
              type='number'
              value={productId}
              onChange={e => {
                setProductId(e.target.value);
                clearPreview();
              }}
              placeholder='e.g. 152402'
            />
          </label>
          <label>
            Domain (optional)
            <input
              type='text'
              value={domain}
              onChange={e => {
                setDomain(e.target.value);
                clearPreview();
              }}
              placeholder='e.g. athleta.gap.com'
            />
          </label>
          <button className='preview-button' onClick={handlePreview} disabled={!parseInt(productId) || isPreviewing}>
            {isPreviewing ? 'Previewing...' : 'Preview'}
          </button>
        </div>
      </div>

      {report?.hasWork && (
        <div className='preview-section'>
          <h2 className='product-heading'>
            {report.productImage && <img src={getSmartImage(report.productImage)} alt='' loading='lazy' />}
            <span>
              Product {report.productId} — {report.productTitle || '(missing product)'}
            </span>
          </h2>

          {report.belowSuspicionGate && (
            <div className='warning-banner'>
              Most URLs look legitimately matched to this product. Double-check this is really a sink before detaching.
            </div>
          )}

          {!!ignoredStemCount && (
            <div className='warning-banner'>
              {ignoredStemCount} of these URLs are on ignored domains (
              {report.ignoredDomains.domains.map(entry => `${entry.domain}: ${entry.count}`).join(', ')}). Ignored domains are excluded
              from matching, so after the detach these URLs go nowhere unless you send them to the agent.
              <label className='route-ignored-toggle'>
                <input type='checkbox' checked={routeIgnoredDomains} onChange={e => setRouteIgnoredDomains(e.target.checked)} />
                Queue them for the agentic pin matcher anyway
              </label>
            </div>
          )}

          {!!strandedPinCount && (
            <div className='warning-banner'>
              {strandedPinCount} pins still carry this product although their URL record moved on (
              {describeStrandedUrlStates(report.pins.strandedByUrlState)}). None of the URL checks above apply to them; they are detached
              together with the URLs below.
            </div>
          )}

          <div className='preview-stats'>
            <div className='stat'>
              <span className='stat-value'>{report.totalRows}</span>
              <span className='stat-label'>Matched URLs</span>
            </div>
            <div className='stat'>
              <span className='stat-value'>{report.keep.count + excludedIds.length}</span>
              <span className='stat-label'>
                Kept ({keepReasons}
                {excludedIds.length ? `, ${excludedIds.length} unchecked below` : ''})
              </span>
            </div>
            <div className='stat'>
              <span className='stat-value'>{adjustedUrlCount}</span>
              <span className='stat-label'>URLs to detach</span>
            </div>
            <div className='stat'>
              <span className='stat-value'>
                {report.variants.toDetach}/{report.variants.total}
              </span>
              <span className='stat-label'>Variants to detach</span>
            </div>
            <div className='stat'>
              <span className='stat-value'>{adjustedPinCount}</span>
              <span className='stat-label'>
                Pins to detach ({strandedPinCount ? `${strandedPinCount} stranded · ` : ''}of {report.pins.totalOnProduct} on product)
              </span>
            </div>
            <div className='stat'>
              <span className='stat-value'>{report.payoutStamps}</span>
              <span className='stat-label'>Payout stamps to clear</span>
            </div>
            <div className='stat'>
              <span className='stat-value'>{report.imageRestores}</span>
              <span className='stat-label'>Pin images to restore</span>
            </div>
          </div>

          {!!report.variants.samples.length && (
            <div className='variant-samples'>
              <h3>Sample variants to detach</h3>
              <div className='variant-chips'>
                {report.variants.samples.map((sample, index) => (
                  <span key={index} className='variant-chip'>
                    {sample.image && <img src={getSmartImage(sample.image)} alt='' loading='lazy' />}
                    {sample.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className='url-table-section'>
            <div className='url-table-header'>
              <h3>URLs to detach</h3>
              <span className='url-table-hint'>Uncheck a URL to keep it (and its pins) attached to the product.</span>
              <input
                type='text'
                className='url-filter'
                value={filter}
                onChange={e => {
                  setFilter(e.target.value);
                  setPage(0);
                }}
                placeholder='Filter by URL, crawled name or pin title'
              />
            </div>
            <table>
              <thead>
                <tr>
                  <th className='checkbox-col'>Detach</th>
                  <th>URL</th>
                  <th>Crawled name</th>
                  <th>Pins</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(row => (
                  <tr key={row.urlDataId} className={cn({ excluded: excludedSet.has(row.urlDataId) })}>
                    <td className='checkbox-col'>
                      <input type='checkbox' checked={!excludedSet.has(row.urlDataId)} onChange={() => toggleExcluded(row.urlDataId)} />
                    </td>
                    <td className='stem-col'>{row.urlStem}</td>
                    <td>{row.crawledName || '-'}</td>
                    <td>
                      {row.pinCount}
                      {!!row.pinSamples.length && (
                        <span className='pin-thumbs'>
                          {row.pinSamples.map(
                            (pin, index) =>
                              pin.image && (
                                <img
                                  key={index}
                                  src={getSmartImage(pin.image)}
                                  alt=''
                                  loading='lazy'
                                  title={`"${pin.title || 'untitled'}" by ${pin.username || 'unknown'}${pin.seeded ? ' (auto-named from this product)' : ''}`}
                                />
                              )
                          )}
                        </span>
                      )}
                      {!!row.pinSamples.length && (
                        <span className='pin-samples'>
                          {row.pinSamples.slice(0, 3).map((pin, index) => (
                            <span key={index} className={cn('pin-sample', { seeded: pin.seeded })}>
                              "{pin.title || 'untitled'}" by {pin.username || 'unknown'}
                              {pin.seeded && ' (auto-named from this product)'}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className='url-table-footer'>
              <span>
                Showing {pageRows.length} of {filteredRows.length} URLs
                {filter ? ` (filtered from ${detachRows.length})` : ''}
              </span>
              {pageCount > 1 && (
                <span className='pagination'>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    ← Prev
                  </button>
                  Page {page + 1} / {pageCount}
                  <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}>
                    Next →
                  </button>
                </span>
              )}
            </div>
          </div>

          <button className='detach-button' onClick={handleDetach} disabled={isStarting || !(adjustedUrlCount || strandedPinCount)}>
            {isStarting
              ? 'Queueing...'
              : `Detach ${[adjustedUrlCount && `${adjustedUrlCount} URLs`, strandedPinCount && `${strandedPinCount} stranded pins`].filter(Boolean).join(' + ')}`}
          </button>
        </div>
      )}

      <div className='runs-section'>
        <h2>Runs</h2>
        <table>
          <thead>
            <tr>
              <th>Run</th>
              <th>Product</th>
              <th>Domain</th>
              <th>Status</th>
              <th>Queued</th>
              <th>Result</th>
              <th>Agent matches</th>
            </tr>
          </thead>
          <tbody>
            {!runs.length && (
              <tr>
                <td colSpan={7} className='empty'>
                  {hasLoadedRuns ? 'No runs yet' : 'Loading runs...'}
                </td>
              </tr>
            )}
            {runs.map(run => (
              <tr key={run.id}>
                <td>{run.id}</td>
                <td>
                  {run.Product_id}
                  {run.report?.productTitle ? ` — ${run.report.productTitle}` : ''}
                </td>
                <td>{run.domain || 'all'}</td>
                <td>
                  <span className={cn('status-pill', run.status)}>{run.status}</span>
                </td>
                <td>{new Date(run.createdAt).toLocaleString()}</td>
                <td>
                  {run.status === 'completed' &&
                    run.totals &&
                    `${run.totals.urlData} URLs, ${run.totals.pins} pins, ${run.totals.variants} variants, ` +
                      (run.totals.stemsQueuedForAgent != null
                        ? `${run.totals.stemsQueuedForAgent} to agent${
                            run.totals.stemsSkippedIgnoredDomain ? `, ${run.totals.stemsSkippedIgnoredDomain} left on ignored domains` : ''
                          }`
                        : `${run.totals.stemsRequeued} requeued`)}
                  {run.status === 'failed' && (run.error || 'failed')}
                </td>
                <td className='agent-outcomes'>
                  {run.agentOutcomes ? (
                    <>
                      <span>
                        {[
                          run.agentOutcomes.queued ? `${run.agentOutcomes.queued} queued` : null,
                          run.agentOutcomes.evaluating ? `${run.agentOutcomes.evaluating} evaluating` : null,
                          `${run.agentOutcomes.applied} applied`,
                          run.agentOutcomes.reverted ? `${run.agentOutcomes.reverted} undone` : null,
                          run.agentOutcomes.skipped ? `${run.agentOutcomes.skipped} skipped` : null
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {run.agentOutcomes.applied > 0 && (
                        <button type='button' className='undo-agent-matches' onClick={() => handleRevertAgentMatches(run)}>
                          Undo {run.agentOutcomes.applied} applied
                        </button>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductSinkDetach;
